/**
 * Directors block — pin + scroll-converge.
 *
 * The directors grid visually staggers cards at sm+ via `margin-top` on the
 * `:nth-child(even)` figures (pushing them ~125% + 10rem down). This component
 * pins the section on desktop and, as the user scrolls, translates the even
 * cards up by exactly that offset so all directors converge to a single top
 * baseline — then releases the pin.
 *
 * Desktop-only (>=640px sm breakpoint). Mobile keeps the static staggered
 * layout. `prefers-reduced-motion` short-circuits — no pin, no animation.
 *
 * CSP mode: no inline expressions; component state lives on `this`, all logic
 * is methods/properties here.
 */
export default function directorsPin() {
    return {
        _tween: null,
        _evenCards: [],
        _gsapReadyHandler: null,
        _mq: null,
        _mqHandler: null,

        init() {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

            this._mq = window.matchMedia('(min-width: 640px)');
            if (!this._mq.matches) {
                // Still bind a listener so a resize up to desktop activates the pin.
                this._mqHandler = (e) => {
                    if (e.matches) this._setup();
                };
                this._mq.addEventListener('change', this._mqHandler);
                return;
            }

            this._setup();
        },

        _setup() {
            if (this._tween) return;

            if (!window.gsap || !window.ScrollTrigger) {
                this._gsapReadyHandler = () => this._setup();
                window.addEventListener('gsap:ready', this._gsapReadyHandler, { once: true });
                return;
            }

            const gsap = window.gsap;

            this._evenCards = [...this.$el.querySelectorAll('figure:nth-child(even)')];
            if (!this._evenCards.length) return;

            this._tween = gsap.fromTo(
                this._evenCards,
                { y: 0 },
                {
                    y: () => -this._measureOffset(),
                    ease: 'none',
                    scrollTrigger: {
                        trigger: this.$el,
                        start: 'top top',
                        end: '+=100%',
                        pin: true,
                        pinSpacing: true,
                        anticipatePin: 1,
                        scrub: 0.4,
                        invalidateOnRefresh: true,
                    },
                }
            );
        },

        _measureOffset() {
            // The even-card margin-top resolves to `calc(125% + 10rem)` in
            // pixels. That's exactly the distance needed to translate a card
            // up onto the top row, so read it directly instead of measuring
            // bounding rects (which would be polluted by any applied transform).
            const first = this._evenCards[0];
            if (!first) return 0;
            return parseFloat(window.getComputedStyle(first).marginTop) || 0;
        },

        destroy() {
            if (this._tween) {
                if (this._tween.scrollTrigger) this._tween.scrollTrigger.kill();
                this._tween.kill();
                this._tween = null;
            }
            if (this._gsapReadyHandler) {
                window.removeEventListener('gsap:ready', this._gsapReadyHandler);
                this._gsapReadyHandler = null;
            }
            if (this._mqHandler && this._mq) {
                this._mq.removeEventListener('change', this._mqHandler);
                this._mqHandler = null;
            }
            if (window.gsap && this._evenCards.length) {
                window.gsap.set(this._evenCards, { clearProps: 'transform' });
            }
        },
    };
}
